import { useQuery } from '@tanstack/react-query';
import { formatPrice, type ProductListItem } from '@printtat/shared';
import { useBuilderOverlay } from '@printtat/builder';
import { queryKeys } from '@/lib/queryKeys';
import { productsApi } from '../../services/productsApi';
import { getDefaultMockupPath } from '../../utils/productAssets';
import { X, ShoppingBag, Sparkles } from 'lucide-react';
import { IconButton } from '@printtat/ui';

interface BaseProductPickerProps {
  onClose: () => void;
  onSave?: (designId: string) => void;
}

export const BaseProductPicker = ({ onClose, onSave }: BaseProductPickerProps) => {
  const openBuilder = useBuilderOverlay((s) => s.openBuilder);
  const { data: products = [], isLoading } = useQuery<ProductListItem[]>({
    queryKey: queryKeys.products.all(),
    queryFn: () => productsApi.getProducts(),
    staleTime: 60_000,
  });

  const handleSelect = (product: ProductListItem) => {
    onClose();
    openBuilder({ productId: product.id, productSlug: product.slug, onSave });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Choose a Product</h2>
            <p className="text-xs text-gray-400 mt-0.5">Select a base product to customize</p>
          </div>
          <IconButton size="sm" onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </IconButton>
        </div>

        <div className="px-6 pb-6">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse rounded-xl border border-gray-100 overflow-hidden">
                  <div className="bg-gray-100 aspect-square" />
                  <div className="p-3 space-y-2">
                    <div className="bg-gray-100 h-3.5 rounded w-2/3" />
                    <div className="bg-gray-100 h-3 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-sm text-gray-400">No products available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelect(product)}
                  className="rounded-lg border border-gray-200 text-left hover:border-gray-300 hover:shadow-xs transition-all duration-200 group cursor-pointer overflow-hidden"
                >
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/80 aspect-square flex items-center justify-center overflow-hidden relative">
                    <img
                      src={getDefaultMockupPath(product.slug, 'front')}
                      alt={product.name}
                      className="w-full h-full object-cover object-top-right group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <span className="flex items-center gap-1.5 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                        <Sparkles size={14} />
                        Start Design
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-sm text-gray-900 truncate">{product.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">From {formatPrice(product.basePrice)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
